/**
 * WaterSystem — reflective/refractive water planes with wave animation.
 *
 * Phase: RenderSub, Priority: 40
 *
 * Two water types:
 * - **Gerstner** (`addWater`): CPU-side vertex displacement with `MeshPhysicalMaterial`.
 *   Cheap, works everywhere, no planar reflections.
 * - **Reflective** (`addReflectiveWater`): Three.js `WaterMesh` addon with GPU-driven
 *   normal-map animation, Fresnel blending, sun specular, and automatic planar
 *   reflections via `reflector()`. Each reflective water body allocates a reflection
 *   render target at `resolutionScale * viewport` (default 0.5).
 */

import * as THREE from 'three/webgpu';
import { WaterMesh } from 'three/addons/objects/WaterMesh.js';
import type { ISystem } from '../ecs/System.js';
import type { World } from '../ecs/World.js';
import { LoopPhase, SubPhase } from '../shared/types.js';
import type { EngineRenderer } from '../render/Renderer.js';
import type { DayNightCycle } from './DayNightCycle.js';
import { Logger } from '../core/Logger.js';
import {
  createWaveSet, createWaveSetFromSpectrum,
  findCrestPoint,
  sampleHeight,
  type GerstnerWave,
  type GerstnerWaveSetConfig,
  type SpectrumWaveSetConfig,
} from './water/GerstnerWaves.js';
import { ParticleEmitter } from '../particles/ParticleEmitter.js';
import { ParticleRenderer } from '../particles/ParticleRenderer.js';
import {
  createRealisticWaterMaterial,
  DEFAULT_REALISTIC_WATER,
  type RealisticWaterHandles,
  type RealisticWaterMaterialConfig,
} from './water/RealisticWaterMaterial.js';
import { FoamAccumulator } from './water/FoamAccumulator.js';
import { RippleField, type RippleFieldConfig } from './water/RippleField.js';
import { OceanFFT, type OceanFFTConfig } from './water/fft/OceanFFT.js';
import { buildInfiniteGridArrays, snapToStep, type InfiniteGridSpec } from './water/infinite-grid.js';
import { CausticsProjector } from './water/CausticsProjector.js';
import { ShallowWaterSolver, type ShallowWaterConfig } from './water/ShallowWaterSolver.js';
import { ShorelineField } from './water/ShorelineField.js';
import { UnderwaterOverlay, type UnderwaterOverlayConfig } from './water/UnderwaterOverlay.js';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface WaterConfig {
  /** Water height in world units */
  height: number;
  /** Water plane size */
  size: THREE.Vector2;
  /** Water color */
  color: THREE.ColorRepresentation;
  /** Opacity (0 = fully transparent) */
  opacity: number;
  /** Wave speed */
  waveSpeed: number;
  /** Wave scale */
  waveScale: number;
  /** Wave amplitude */
  waveAmplitude: number;
  /** Number of wave octaves */
  waveOctaves: number;
  /** Enable reflections (requires extra render pass) */
  reflections: boolean;
  /** Reflection texture resolution */
  reflectionResolution: number;
  /** Fresnel power */
  fresnelPower: number;
  /** Foam threshold */
  foamThreshold: number;
}

export interface ReflectiveWaterConfig {
  /** Water height in world units */
  height: number;
  /** Water plane size (x = width, y = depth) */
  size: THREE.Vector2;
  /** Water body color (default 0x001e0f) */
  waterColor: THREE.ColorRepresentation;
  /** Sun specular highlight color (default 0xffffff) */
  sunColor: THREE.ColorRepresentation;
  /** Optional normal map texture — procedural fallback generated if omitted */
  waterNormals?: THREE.Texture;
  /** Opacity (default 1.0) */
  alpha: number;
  /** Normal map repeat density (default 10.0) */
  normalScale: number;
  /** Reflection distortion amount (default 3.7) */
  distortionScale: number;
  /** Reflection render target scale relative to viewport (default 0.5) */
  resolutionScale: number;
  /** Optional CPU Gerstner amplitude for buoyancy queries (default 0 = flat) */
  gerstnerAmplitude: number;
  /** Optional CPU Gerstner wave speed (default 1) */
  gerstnerSpeed: number;
  /** Optional CPU Gerstner wave scale (default 4) */
  gerstnerScale: number;
  /** Optional CPU Gerstner octave count (default 0 = disabled) */
  gerstnerOctaves: number;
}

export interface RealisticWaterConfig
  extends Partial<Omit<RealisticWaterMaterialConfig, 'normalMap' | 'fft' | 'rippleTexture' | 'foamAccumTexture' | 'shoreline'>> {
  /** Water height in world units. */
  height: number;
  /** Water plane size (x = width, y = depth). */
  size: THREE.Vector2;
  /** Gerstner wave-set parameters (drives BOTH the shader and height queries). */
  waves?: GerstnerWaveSetConfig;
  /**
   * Phillips-spectrum sampling (Tessendorf 2001) instead of the artist
   * cascade — wind speed/direction set the sea state. Wins over `waves`.
   */
  spectrum?: SpectrumWaveSetConfig;
  /** Grid segments for vertex displacement (default 128, clamped 16..256). */
  segments?: number;
  /** Optional detail normal map; procedural fallback generated if omitted. */
  waterNormals?: THREE.Texture;
  /** Explicit sun direction (overrides DayNightCycle when set). */
  sunDirection?: THREE.Vector3;
  /**
   * Whitecap-trail persistence in seconds (foam lingers and decays after the
   * crest passes, via a CPU accumulation texture). 0 disables the
   * accumulator — instantaneous Jacobian foam only. Default 2.5.
   */
  foamPersistence?: number;
  /**
   * Interactive surface ripples (rain, wakes, splashes): a damped 2-D wave
   * field layered onto the Gerstner waves. `true` for defaults, or tune
   * resolution / speed / damping. Drive with disturbWater()/setRainRate().
   */
  ripples?: boolean | Pick<RippleFieldConfig, 'resolution' | 'waveSpeed' | 'damping'>;
  /**
   * FFT ocean (Tessendorf cascades on TSL compute) — the full broad-spectrum
   * surface instead of <=16 Gerstner components. WebGPU only; WebGL2 falls
   * back to the spectrum-sampled Gerstner path automatically. `true` uses
   * defaults (or the `spectrum` block's wind/model when present).
   */
  fft?: boolean | OceanFFTConfig;
  /**
   * Infinite ocean: a camera-following exponential grid to the horizon
   * (default extent 4 km) instead of a fixed plane. Wave phase stays
   * world-anchored; displacement fades near the horizon against aliasing.
   * `size` is ignored; foam trails + interactive ripples are unavailable
   * (their textures are plane-local; the plane follows the camera).
   */
  infinite?: boolean | InfiniteGridSpec;
  /**
   * Caustic technique: 'voronoi' (default — cheap animated shimmer) or
   * 'projected' (a per-frame refracted-ray light map; Evan Wallace's
   * derivative method). Projected needs a fixed footprint (not infinite).
   */
  causticMode?: 'voronoi' | 'projected';
  /** Projected caustics: receiver depth below the surface in metres. Default 2. */
  floorDepth?: number;
  /**
   * Crest spray: droplet particles erupt where the surface folds (the
   * emitter follows the most compressed sampled crest each frame). Gerstner
   * modes only — the FFT Jacobian lives on the GPU.
   */
  spray?: boolean | { rate?: number; threshold?: number };
  /**
   * Terrain-aware shoreline: bakes a bed-depth field from a terrain height
   * function (e.g. `(x, z) => terrain.sampleHeightAt(x, z)`) enabling wave
   * SHOALING (swell dies on the beach — mirrored by getWaterHeightAt so
   * buoyancy agrees), shallow sand tinting, and a surf-foam band. Fixed
   * footprints only (the baked field can't follow an infinite ocean).
   * Coordinates are body-local (== world while the mesh sits at XZ origin).
   */
  shoreline?: {
    /** Terrain/bed height at (x, z) in world units. */
    bedHeight: (x: number, z: number) => number;
    /** Bake resolution per side. Default 128 (clamped 16..512). */
    resolution?: number;
    /** Depth below which waves attenuate. Default 6 m. */
    shoalDepth?: number;
    /** Bed depth covered by surf foam. Default 1.2 m. */
    foamDepth?: number;
    /** Sand tint for shallows; null disables tinting. Default 0xc2b280. */
    shallowTint?: THREE.ColorRepresentation | null;
  };
  /**
   * Tint the scene with absorption-colored fog while the main camera is
   * submerged in this body (restored on surfacing). Default true.
   */
  underwaterFog?: boolean;
  /**
   * Fullscreen SUBMERGED-CAMERA effect while inside this body: sine-wobble
   * screen distortion, depth-graded absorption tint, near-surface caustic
   * shimmer and a vignette (UnderwaterOverlay). Colors default to this
   * body's deep/shallow/absorption palette. `false` disables. Default true.
   */
  underwaterOverlay?: boolean | UnderwaterOverlayConfig;
  /** Exponential fog density when submerged. Default: mean absorption × 0.4. */
  underwaterFogDensity?: number;
}

export interface ShallowWaterBodyConfig extends Omit<ShallowWaterConfig, 'sizeX' | 'sizeZ'> {
  /** Bed origin height in world units (bedHeight values add to this). */
  height: number;
  /** Domain size (x = width, y = depth). */
  size: THREE.Vector2;
  color: THREE.ColorRepresentation;
  opacity: number;
}

// ─── Internal discriminated union ───────────────────────────────────────────

interface GerstnerWaterBody {
  type: 'gerstner';
  name: string;
  config: WaterConfig;
  mesh: THREE.Mesh;
  material: THREE.MeshPhysicalMaterial;
}

interface ReflectiveWaterBody {
  type: 'reflective';
  name: string;
  config: ReflectiveWaterConfig;
  mesh: WaterMesh;
  /** True if we generated the normal map (we must dispose it) */
  ownsNormalMap: boolean;
  normalMapTexture: THREE.Texture;
}

interface RealisticWaterBody {
  type: 'realistic';
  name: string;
  config: RealisticWaterConfig;
  mesh: THREE.Mesh;
  handles: RealisticWaterHandles;
  waves: GerstnerWave[];
  ownsNormalMap: boolean;
  normalMapTexture: THREE.Texture;
  /** Persistent whitecap trails (present when foamPersistence > 0). */
  foam?: FoamAccumulator;
  /** Interactive ripple field (present when config.ripples is set). */
  ripples?: RippleField;
  /** Raindrops per second currently splashing this body. */
  rainRate?: number;
  /** FFT ocean pipeline (present when config.fft is set on WebGPU). */
  ocean?: OceanFFT;
  /** Camera snap step for infinite bodies (undefined = finite plane). */
  snapStep?: number;
  /** Projected caustics pass (causticMode: 'projected'). */
  caustics?: CausticsProjector;
  /** Crest spray emitter (config.spray, Gerstner modes). */
  spray?: { emitter: ParticleEmitter; renderer: ParticleRenderer; group: THREE.Group; rate: number; threshold: number };
  /** Baked bed-depth field (config.shoreline) — drives shoaling/tint/surf. */
  shoreline?: ShorelineField;
  /** Submerged-camera screen effect (lazy — built on first submerge). */
  underwaterOverlay?: UnderwaterOverlay;
}

interface ShallowWaterBody {
  type: 'swe';
  name: string;
  config: ShallowWaterBodyConfig;
  mesh: THREE.Mesh;
  material: THREE.MeshPhysicalMaterial;
  solver: ShallowWaterSolver;
}

type WaterBodyEntry = GerstnerWaterBody | ReflectiveWaterBody | RealisticWaterBody | ShallowWaterBody;

// ─── Procedural normal map ──────────────────────────────────────────────────

/**
 * The baked bytes per resolution. WHY a cache: the bake is a pure function of the resolution (65,536 texels × 4 cosines
 * at 256²) and its callers re-bake it — Atlas's river rebuilds every 400 m, the water look per material — so a measured
 * ~6 % of a 2.7 s main-thread stretch went to recomputing identical bytes. Each call still hands out a FRESH texture over
 * its OWN copy of the bytes (callers dispose their texture; a shared array would let one caller's edit reach another).
 */
const waterNormalBytes = new Map<number, Uint8Array>();

/**
 * Generate a tileable RGBA DataTexture with layered sine-wave normals.
 * Runs once at water creation time, not per-frame.
 */
export function generateWaterNormalMap(resolution = 256): THREE.DataTexture {
  const size = resolution;
  let baked = waterNormalBytes.get(size);
  if (!baked) {
    baked = bakeWaterNormalBytes(size);
    if (waterNormalBytes.size >= 4) waterNormalBytes.clear(); // callers use one or two sizes; never grow unbounded
    waterNormalBytes.set(size, baked);
  }
  const data = baked.slice();

  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  // WHY explicit filters: DataTexture defaults to NearestFilter with no
  // mipmaps — nearest-sampled 2 cm texels read as square pixels at arm's
  // length and shimmer at distance. Bilinear + trilinear mips (256 is POT)
  // + a little anisotropy give smooth detail at every viewing angle.
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = 4;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** The bake itself — unchanged arithmetic (the unit test pins it byte for byte against the original). */
function bakeWaterNormalBytes(size: number): Uint8Array {
  const data = new Uint8Array(size * size * 4);
  const TAU = Math.PI * 2;

  // 4 octaves of directional sine waves at different frequencies and angles.
  // WHY integer cycle counts: the texture is used with RepeatWrapping, and a
  // sine with a NON-integer number of cycles across the tile (the old 0.7,
  // -0.5, 1.3, … set) does not wrap — every repeat boundary became a hard C0
  // seam, drawing a crisp world-aligned square lattice over the water at the
  // repeat pitch (the "square tiles" artifact, user screenshot 2026-08-23).
  // Any integer (kx, kz) tiles seamlessly by construction; the phase offsets
  // keep the octaves from sharing a zero-crossing at the tile origin.
  const octaves = [
    { kx: 1, kz: 1, amp: 0.4, ph: 0.0 },
    { kx: -1, kz: 2, amp: 0.25, ph: 1.7 },
    { kx: 2, kz: -1, amp: 0.2, ph: 3.9 },
    { kx: -2, kz: 3, amp: 0.15, ph: 5.1 },
  ];

  for (let j = 0; j < size; j++) {
    for (let i = 0; i < size; i++) {
      const u = i / size;
      const v = j / size;

      let dhdx = 0;
      let dhdz = 0;

      for (const oct of octaves) {
        const phase = (u * oct.kx + v * oct.kz) * TAU + oct.ph;
        // derivative of amp * sin(phase) w.r.t. u and v
        const cosPhase = Math.cos(phase) * oct.amp * TAU;
        dhdx += cosPhase * oct.kx;
        dhdz += cosPhase * oct.kz;
      }

      // Normal = normalize(-dhdx, bias, -dhdz) — bias keeps normal mostly upward
      const bias = 4.0;
      const nx = -dhdx;
      const ny = bias;
      const nz = -dhdz;
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);

      // Pack to [0, 255] range: n * 0.5 + 0.5
      const idx = (j * size + i) * 4;
      data[idx] = ((nx / len) * 0.5 + 0.5) * 255;
      data[idx + 1] = ((ny / len) * 0.5 + 0.5) * 255;
      data[idx + 2] = ((nz / len) * 0.5 + 0.5) * 255;
      data[idx + 3] = 255;
    }
  }
  return data;
}

// ─── Water System ───────────────────────────────────────────────────────────

export class WaterSystem implements ISystem {
  readonly name = 'WaterSystem';
  readonly phase = LoopPhase.RenderSub;
  readonly subPhase = SubPhase.Update;
  readonly priority = 40;

  private waters = new Map<string, WaterBodyEntry>();
  private parentGroup = new THREE.Group();
  private time = 0;
  private _sunDir = new THREE.Vector3();
  private scene: THREE.Scene | null = null;
  /** Fog that was on the scene before we submerged (restored on surfacing). */
  private _prevFog: THREE.Scene['fog'] = null;
  /** The one FogExp2 reused across every submerge when the scene has none. */
  private _ownFog: THREE.FogExp2 | null = null;
  /** When the scene already had a FogExp2, we MUTATE it (swapping the fog
   *  OBJECT forces every material's program to rebuild — a 1–2 s hitch on
   *  BOTH submerge and surface); these are the values to restore. */
  private _mutatedFog: THREE.FogExp2 | null = null;
  private _prevFogValues: { color: number; density: number } | null = null;
  private _underwaterBody: string | null = null;
  private readonly _camPos = new THREE.Vector3();

  constructor(
    private renderer: EngineRenderer,
    private dayNightCycle?: DayNightCycle,
  ) {
    this.parentGroup.name = 'Water';
  }

  init(world: World): void {
    this.scene = world.scene;
    world.scene.add(this.parentGroup);
  }

  // ─── Gerstner water (backward-compatible) ───────────────────────────

  /**
   * Add a CPU Gerstner water body.
   *
   * @deprecated Legacy type: its height query uses sine formulas that only
   * loosely track the visuals. Prefer `addRealisticWater` — one wave set
   * drives both the shader and `getWaterHeightAt` exactly.
   */
  addWater(name: string, config: Partial<WaterConfig> = {}): THREE.Mesh {
    const existing = this.waters.get(name);
    if (existing) {
      Logger.warn('Water', `Water body "${name}" already exists — returning existing mesh.`);
      return existing.mesh;
    }

    const cfg: WaterConfig = {
      height: config.height ?? 0,
      size: config.size ?? new THREE.Vector2(100, 100),
      color: config.color ?? 0x001e0f,
      opacity: config.opacity ?? 0.8,
      waveSpeed: config.waveSpeed ?? 1,
      waveScale: config.waveScale ?? 4,
      waveAmplitude: config.waveAmplitude ?? 0.15,
      waveOctaves: Math.min(Math.max(Math.floor(config.waveOctaves ?? 3), 1), 8),
      reflections: config.reflections ?? false,
      reflectionResolution: config.reflectionResolution ?? 512,
      fresnelPower: config.fresnelPower ?? 2,
      foamThreshold: config.foamThreshold ?? 0.8,
    };

    // Create water geometry
    const segs = Math.min(Math.max(Math.round(Math.max(cfg.size.x, cfg.size.y) / 2), 8), 128);
    const geometry = new THREE.PlaneGeometry(
      cfg.size.x, cfg.size.y,
      segs, segs,
    );
    geometry.rotateX(-Math.PI / 2);

    // Create water material using MeshPhysicalMaterial for PBR water
    const material = new THREE.MeshPhysicalMaterial({
      color: cfg.color,
      transparent: true,
      opacity: cfg.opacity,
      roughness: 0.0,
      metalness: 0.1,
      transmission: 0.9,
      thickness: 1.5,
      ior: 1.33, // Water IOR
      envMapIntensity: 1.0,
      side: THREE.DoubleSide,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.y = cfg.height;
    mesh.name = `Water_${name}`;
    mesh.receiveShadow = true;

    const waterBody: GerstnerWaterBody = { type: 'gerstner', name, config: cfg, mesh, material };

    this.parentGroup.add(mesh);
    this.waters.set(name, waterBody);

    Logger.debug('Water', `Added Gerstner water body "${name}" at y=${cfg.height}`);
    return mesh;
  }

  // ─── Reflective water (WaterMesh addon) ─────────────────────────────

  /**
   * Add a reflective water body using Three.js WaterMesh with GPU-driven
   * normal animation and planar reflections.
   *
   * NOTE: the optional `gerstner*` height parameters approximate the
   * WaterMesh visuals for buoyancy but are not derived from them; for exact
   * physics/visual agreement use `addRealisticWater`.
   */
  addReflectiveWater(name: string, config: Partial<ReflectiveWaterConfig> = {}): WaterMesh {
    const existing = this.waters.get(name);
    if (existing) {
      Logger.warn('Water', `Water body "${name}" already exists — returning existing mesh.`);
      return existing.mesh as WaterMesh;
    }

    const cfg: ReflectiveWaterConfig = {
      height: config.height ?? 0,
      size: config.size ?? new THREE.Vector2(100, 100),
      waterColor: config.waterColor ?? 0x001e0f,
      sunColor: config.sunColor ?? 0xffffff,
      waterNormals: config.waterNormals,
      alpha: config.alpha ?? 1.0,
      normalScale: config.normalScale ?? 10.0,
      distortionScale: config.distortionScale ?? 3.7,
      resolutionScale: config.resolutionScale ?? 0.5,
      gerstnerAmplitude: config.gerstnerAmplitude ?? 0,
      gerstnerSpeed: config.gerstnerSpeed ?? 1,
      gerstnerScale: config.gerstnerScale ?? 4,
      gerstnerOctaves: Math.min(Math.max(Math.floor(config.gerstnerOctaves ?? 0), 0), 8),
    };

    // Resolve normal map: use user-provided or generate procedural fallback
    let normalMapTexture: THREE.Texture;
    let ownsNormalMap: boolean;
    if (cfg.waterNormals) {
      normalMapTexture = cfg.waterNormals;
      ownsNormalMap = false;
    } else {
      normalMapTexture = generateWaterNormalMap(256);
      ownsNormalMap = true;
    }

    // Create geometry
    const geometry = new THREE.PlaneGeometry(cfg.size.x, cfg.size.y);
    geometry.rotateX(-Math.PI / 2);

    // Resolve initial sun direction
    const sunDir = this.dayNightCycle
      ? new THREE.Vector3().copy(this.dayNightCycle.getSunDirection())
      : new THREE.Vector3(0.70707, 0.70707, 0);

    // Construct WaterMesh
    const mesh = new WaterMesh(geometry, {
      waterNormals: normalMapTexture,
      sunDirection: sunDir,
      sunColor: cfg.sunColor,
      waterColor: cfg.waterColor,
      alpha: cfg.alpha,
      size: cfg.normalScale,
      distortionScale: cfg.distortionScale,
      resolutionScale: cfg.resolutionScale,
    });

    mesh.position.y = cfg.height;
    mesh.name = `Water_${name}`;
    mesh.receiveShadow = true;

    const entry: ReflectiveWaterBody = {
      type: 'reflective',
      name,
      config: cfg,
      mesh,
      ownsNormalMap,
      normalMapTexture,
    };

    this.parentGroup.add(mesh);
    this.waters.set(name, entry);

    Logger.debug('Water', `Added reflective water body "${name}" at y=${cfg.height}`);
    return mesh;
  }

  // ─── Realistic water (TSL: Gerstner + refraction/absorption/foam/caustics) ──

  /**
   * Add the hyper-realistic water type: TSL Gerstner displacement, planar
   * reflection, screen-space refraction with Beer–Lambert absorption,
   * shoreline + crest foam, Voronoi caustic shimmer and sun glints. The SAME
   * wave set drives `getWaterHeightAt`, so buoyancy matches the visuals.
   * Technique sources are documented in water/RealisticWaterMaterial.ts.
   */
  addRealisticWater(name: string, config: Partial<RealisticWaterConfig> = {}): THREE.Mesh {
    const existing = this.waters.get(name);
    if (existing) {
      Logger.warn('Water', `Water body "${name}" already exists — returning existing mesh.`);
      return existing.mesh;
    }

    const cfg: RealisticWaterConfig = {
      ...DEFAULT_REALISTIC_WATER,
      ...config,
      height: config.height ?? 0,
      size: config.size ?? new THREE.Vector2(100, 100),
    };

    let normalMapTexture: THREE.Texture;
    let ownsNormalMap: boolean;
    if (cfg.waterNormals) {
      normalMapTexture = cfg.waterNormals;
      ownsNormalMap = false;
    } else {
      normalMapTexture = generateWaterNormalMap(256);
      ownsNormalMap = true;
    }

    // FFT ocean (WebGPU) supersedes the Gerstner wave set; its CPU buoyancy
    // mirror keeps height queries working. WebGL2 falls back to spectrum
    // Gerstner — the same gate the splat GPU sort uses.
    let ocean: OceanFFT | undefined;
    let waves: GerstnerWave[];
    if (cfg.fft) {
      const fftCfg: OceanFFTConfig = typeof cfg.fft === 'object' ? cfg.fft : { ...(cfg.spectrum ?? {}) };
      if (this.renderer.isWebGPU) {
        ocean = new OceanFFT(this.renderer, fftCfg);
        waves = ocean.buoyancyWaves;
      } else {
        Logger.warn('Water', `FFT ocean "${name}" needs WebGPU — using spectrum Gerstner fallback.`);
        waves = createWaveSetFromSpectrum({ ...fftCfg, count: 12 });
      }
    } else {
      waves = cfg.spectrum ? createWaveSetFromSpectrum(cfg.spectrum) : createWaveSet(cfg.waves);
    }

    let geometry: THREE.BufferGeometry;
    let snapStep: number | undefined;
    let extent = 0;
    if (cfg.infinite) {
      // Camera-following exponential grid; the plane-UV features that assume
      // a FIXED footprint can't apply (their textures would drag with the
      // camera), so they're disabled below.
      const grid = buildInfiniteGridArrays(typeof cfg.infinite === 'object' ? cfg.infinite : {});
      geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(grid.positions, 3));
      geometry.setAttribute('uv', new THREE.BufferAttribute(grid.uvs, 2));
      // Flat +Y base normals: the material's analytic wave normal replaces
      // them, but TSL's normal accessor still reads the attribute and warns
      // (then zero-fills) when a geometry ships without one.
      const up = new Float32Array(grid.positions.length);
      for (let i = 1; i < up.length; i += 3) up[i] = 1;
      geometry.setAttribute('normal', new THREE.BufferAttribute(up, 3));
      geometry.setIndex(new THREE.BufferAttribute(grid.indices, 1));
      snapStep = grid.snapStep;
      extent = grid.extent;
    } else {
      const segments = Math.min(Math.max(Math.floor(cfg.segments ?? 128), 16), 256);
      const plane = new THREE.PlaneGeometry(cfg.size.x, cfg.size.y, segments, segments);
      plane.rotateX(-Math.PI / 2);
      geometry = plane;
    }

    // Persistent whitecap trails: CPU accumulator over the plane, sampled by
    // the material alongside the per-pixel instantaneous Jacobian mask.
    // (FFT mode skips it — its buoyancy-mirror waves carry no chop, so the
    // CPU Jacobian would read ≈1; GPU whitecaps come from the J texture.)
    const persistence = cfg.foamPersistence ?? 2.5;
    const foam =
      !ocean && !cfg.infinite && persistence > 0
        ? new FoamAccumulator(waves, { sizeX: cfg.size.x, sizeZ: cfg.size.y, persistence })
        : undefined;

    // Interactive ripples (rain/wakes) — opt-in; plane-local, so finite only.
    if (cfg.ripples && cfg.infinite) {
      Logger.warn('Water', `"${name}": ripples need a fixed footprint — ignored on an infinite ocean.`);
    }
    const ripples =
      cfg.ripples && !cfg.infinite
        ? new RippleField({
            sizeX: cfg.size.x,
            sizeZ: cfg.size.y,
            ...(typeof cfg.ripples === 'object' ? cfg.ripples : {}),
          })
        : undefined;

    // Terrain-aware shoreline: bake the bed-depth field once (terrain is
    // static; the mesh sits at the XZ origin so local == world).
    if (cfg.shoreline && cfg.infinite) {
      Logger.warn('Water', `"${name}": shoreline needs a fixed footprint — ignored on an infinite ocean.`);
    }
    const shoreline =
      cfg.shoreline && !cfg.infinite
        ? new ShorelineField({
            sizeX: cfg.size.x,
            sizeZ: cfg.size.y,
            waterLevel: cfg.height,
            bedHeight: cfg.shoreline.bedHeight,
            resolution: cfg.shoreline.resolution,
            shoalDepth: cfg.shoreline.shoalDepth,
            foamDepth: cfg.shoreline.foamDepth,
          })
        : undefined;

    // Projected caustics: real refracted-ray light map over the footprint.
    if (cfg.causticMode === 'projected' && cfg.infinite) {
      Logger.warn('Water', `"${name}": projected caustics need a fixed footprint — using voronoi.`);
    }
    const caustics =
      cfg.causticMode === 'projected' && !cfg.infinite
        ? new CausticsProjector({
            sizeX: cfg.size.x,
            sizeZ: cfg.size.y,
            floorDepth: cfg.floorDepth,
            strength: cfg.causticIntensity ?? DEFAULT_REALISTIC_WATER.causticIntensity,
            ...(ocean
              ? {
                  fft: {
                    displacement: ocean.displacementTextures,
                    derivatives: ocean.derivativeTextures,
                    patchSizes: ocean.patchSizes,
                  },
                }
              : { waves }),
          })
        : undefined;

    const handles = createRealisticWaterMaterial(
      {
        ...(cfg as Required<RealisticWaterConfig>),
        // WebGPU doctrine: no viewport scene copies — r182's mid-pass copy
        // corrupts encoder state (see RealisticWaterMaterialConfig docs).
        screenSpaceRefraction: !this.renderer.isWebGPU,
        normalMap: normalMapTexture,
        foamAccumTexture: foam?.texture ?? null,
        rippleTexture: ripples?.texture ?? null,
        causticTexture: caustics?.texture ?? null,
        fft: ocean
          ? {
              displacement: ocean.displacementTextures,
              derivatives: ocean.derivativeTextures,
              patchSizes: ocean.patchSizes,
            }
          : null,
        distanceFade: cfg.infinite ? { start: extent * 0.25, end: extent * 0.85 } : null,
        shoreline: shoreline
          ? {
              depthTexture: shoreline.texture,
              encodeMax: shoreline.encodeMax,
              shoalDepth: shoreline.shoalDepth,
              foamDepth: shoreline.foamDepth,
              shallowTint: cfg.shoreline!.shallowTint === undefined ? 0xc2b280 : cfg.shoreline!.shallowTint,
            }
          : null,
      },
      waves,
    );
    const mesh = new THREE.Mesh(geometry, handles.material);
    mesh.position.y = cfg.height;
    mesh.name = `Water_${name}`;
    if (cfg.infinite) mesh.frustumCulled = false; // camera-centered + displaced
    // The reflector's virtual camera target must live under the water mesh
    // (three r182 Water2Mesh idiom).
    if (handles.reflectorTarget) mesh.add(handles.reflectorTarget);

    const sunDir = cfg.sunDirection
      ? cfg.sunDirection.clone().normalize()
      : this.dayNightCycle
        ? new THREE.Vector3().copy(this.dayNightCycle.getSunDirection())
        : new THREE.Vector3(0.4, 0.8, 0.2).normalize();
    handles.uSunDirection.value.copy(sunDir);
    // The projector refracts its pattern along ITS sun uniform — an explicit
    // config.sunDirection never reached it (the per-frame sync skips bodies
    // with an authored sun), so caustics stayed on the default (0.4, 0.8, 0.2).
    if (caustics) caustics.uSunDirection.value.copy(sunDir);

    // Crest spray: droplets erupting at folding crests (Gerstner modes).
    let spray: RealisticWaterBody['spray'];
    if (cfg.spray && !ocean) {
      const sprayCfg = typeof cfg.spray === 'object' ? cfg.spray : {};
      const emitter = new ParticleEmitter({
        maxParticles: 400,
        lifetime: { min: 0.35, max: 0.9 },
        startSpeed: { min: 1.2, max: 2.6 },
        startSize: { min: 0.03, max: 0.09 },
        startColor: 0xf2f8fb,
        gravityModifier: 1.1,
        simulationSpace: 'local',
        shape: { type: 'hemisphere', radius: 0.5 },
        emission: { rateOverTime: 0 },
      });
      const sprayRenderer = new ParticleRenderer(emitter.config);
      sprayRenderer.mesh.frustumCulled = false;
      const group = new THREE.Group();
      group.add(sprayRenderer.mesh);
      mesh.add(group);
      spray = {
        emitter,
        renderer: sprayRenderer,
        group,
        rate: sprayCfg.rate ?? 120,
        threshold: sprayCfg.threshold ?? 0.5,
      };
    } else if (cfg.spray && ocean) {
      Logger.warn('Water', `"${name}": crest spray follows the CPU Jacobian — unavailable in FFT mode.`);
    }

    // Submerged-camera overlay: built EAGERLY (not on first submerge) so its
    // pipeline can be pre-warmed — a first-use compile at the submerge moment
    // is a visible hitch. Hidden until the camera actually goes under.
    let underwaterOverlay: UnderwaterOverlay | undefined;
    if (cfg.underwaterOverlay !== false) {
      const overlayCfg = typeof cfg.underwaterOverlay === 'object' ? cfg.underwaterOverlay : {};
      underwaterOverlay = new UnderwaterOverlay({
        deepColor: cfg.deepColor ?? DEFAULT_REALISTIC_WATER.deepColor,
        shallowColor: cfg.shallowColor ?? DEFAULT_REALISTIC_WATER.shallowColor,
        absorption: cfg.absorption ?? DEFAULT_REALISTIC_WATER.absorption,
        ...overlayCfg,
      });
      this.parentGroup.add(underwaterOverlay.mesh);
      // Fire-and-forget pipeline warm (r158+ Renderer.compileAsync); harmless
      // no-op where unavailable (tests / minimal renderers).
      const native = this.renderer.native as unknown as {
        compileAsync?: (o: unknown, c?: unknown, s?: unknown) => Promise<unknown>;
      };
      try {
        void native.compileAsync?.(underwaterOverlay.mesh, undefined, this.scene)?.catch(() => {});
      } catch { /* renderer without compileAsync */ }
    }

    const entry: RealisticWaterBody = {
      type: 'realistic',
      name,
      config: cfg,
      mesh,
      handles,
      waves,
      ownsNormalMap,
      normalMapTexture,
      foam,
      ripples,
      ocean,
      snapStep,
      caustics,
      spray,
      shoreline,
      underwaterOverlay,
    };
    this.parentGroup.add(mesh);
    this.waters.set(name, entry);
    Logger.debug('Water', `Added realistic water body "${name}" at y=${cfg.height}`);
    return mesh;
  }

  // ─── Shallow-water (virtual pipes SWE) ──────────────────────────────

  /**
   * Add a SHALLOW-WATER body: a virtual-pipes SWE simulation over a bed —
   * pooling, sloshing, dam breaks, run-up, sources and drains. The mesh is a
   * CPU heightfield tracking the solver every frame. Use `addWaterAt` to
   * pour/drain; `getWaterHeightAt` reads the live surface.
   */
  addShallowWater(name: string, config: Partial<ShallowWaterBodyConfig> = {}): THREE.Mesh {
    const existing = this.waters.get(name);
    if (existing) {
      Logger.warn('Water', `Water body "${name}" already exists — returning existing mesh.`);
      return existing.mesh;
    }
    const cfg: ShallowWaterBodyConfig = {
      height: config.height ?? 0,
      size: config.size ?? new THREE.Vector2(20, 20),
      color: config.color ?? 0x2e6f8b,
      opacity: config.opacity ?? 0.85,
      resolution: config.resolution ?? 96,
      bedHeight: config.bedHeight,
      initialDepth: config.initialDepth,
      damping: config.damping,
      openBoundaries: config.openBoundaries,
      gravity: config.gravity,
    };
    const solver = new ShallowWaterSolver({
      sizeX: cfg.size.x,
      sizeZ: cfg.size.y,
      resolution: cfg.resolution,
      bedHeight: cfg.bedHeight,
      initialDepth: cfg.initialDepth,
      damping: cfg.damping,
      openBoundaries: cfg.openBoundaries,
      gravity: cfg.gravity,
    });

    const n = solver.resolution;
    const geometry = new THREE.PlaneGeometry(cfg.size.x, cfg.size.y, n - 1, n - 1);
    geometry.rotateX(-Math.PI / 2);
    const material = new THREE.MeshPhysicalMaterial({
      color: cfg.color,
      transparent: true,
      opacity: cfg.opacity,
      roughness: 0.05,
      metalness: 0.05,
      transmission: 0.6,
      ior: 1.33,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.y = cfg.height;
    mesh.name = `Water_${name}`;
    mesh.frustumCulled = false; // heights move every frame

    const entry: ShallowWaterBody = { type: 'swe', name, config: cfg, mesh, material, solver };
    this.parentGroup.add(mesh);
    this.waters.set(name, entry);
    Logger.debug('Water', `Added shallow-water body "${name}" at y=${cfg.height}`);
    return mesh;
  }

  /** Pour (positive) or drain (negative) water on a shallow-water body [m³]. */
  addWaterAt(name: string, worldX: number, worldZ: number, radius: number, amount: number): boolean {
    const entry = this.waters.get(name);
    if (!entry || entry.type !== 'swe') return false;
    entry.solver.addWater(worldX - entry.mesh.position.x, worldZ - entry.mesh.position.z, radius, amount);
    return true;
  }

  /** Direct access to a shallow-water body's solver (custom bed coupling…). */
  getShallowSolver(name: string): ShallowWaterSolver | undefined {
    const entry = this.waters.get(name);
    return entry?.type === 'swe' ? entry.solver : undefined;
  }

  // ─── Removal ────────────────────────────────────────────────────────

  /**
   * Remove a water body by name.
   */
  removeWater(name: string): void {
    const entry = this.waters.get(name);
    if (!entry) return;

    entry.mesh.removeFromParent();
    entry.mesh.geometry.dispose();

    if (entry.type === 'gerstner') {
      entry.material.dispose();
    } else if (entry.type === 'realistic') {
      entry.foam?.dispose();
      entry.ripples?.dispose();
      entry.ocean?.dispose();
      entry.caustics?.dispose();
      entry.shoreline?.dispose();
      if (entry.underwaterOverlay) {
        entry.underwaterOverlay.mesh.removeFromParent();
        entry.underwaterOverlay.dispose();
      }
      entry.spray?.renderer.dispose?.();
      entry.handles.material.dispose();
      entry.mesh.traverse((child) => {
        if ((child as any).isReflector && (child as any).renderTarget) {
          (child as any).renderTarget.dispose();
        }
      });
      if (entry.ownsNormalMap) entry.normalMapTexture.dispose();
    } else if (entry.type === 'swe') {
      entry.material.dispose();
    } else {
      // Reflective: dispose node material
      entry.mesh.material.dispose();
      // Dispose reflector render target (attached as child by WaterMesh)
      entry.mesh.traverse((child) => {
        if ((child as any).isReflector && (child as any).renderTarget) {
          (child as any).renderTarget.dispose();
        }
      });
      // Only dispose normal map if we generated it
      if (entry.ownsNormalMap) {
        entry.normalMapTexture.dispose();
      }
    }

    this.waters.delete(name);
  }

  // ─── Height query (buoyancy) ────────────────────────────────────────

  /**
   * Get water height at a given world position (for buoyancy queries).
   * Works transparently for both Gerstner and reflective water types.
   */
  getWaterHeightAt(name: string, x: number, z: number): number {
    const entry = this.waters.get(name);
    if (!entry) return 0;

    if (entry.type === 'gerstner') {
      const cfg = entry.config;
      let height = cfg.height;
      for (let o = 0; o < cfg.waveOctaves; o++) {
        const scale = Math.pow(2, o);
        const freq = cfg.waveScale * scale;
        const amp = cfg.waveAmplitude / scale;
        height += Math.sin(x * freq * 0.1 + this.time * cfg.waveSpeed * (o + 1)) * amp;
        height += Math.cos(z * freq * 0.13 + this.time * cfg.waveSpeed * (o + 0.7)) * amp * 0.7;
      }
      return height;
    }

    if (entry.type === 'realistic') {
      // Same wave set + SAME clock as the shader — physics matches visuals.
      const local = entry.mesh.position;
      const lx = x - local.x;
      const lz = z - local.z;
      // Shoaling mirrors the shader's smoothstep on the same baked field —
      // buoyancy feels the waves die at the beach exactly like the mesh.
      const shoal = entry.shoreline?.attenuationAt(lx, lz) ?? 1;
      return (
        entry.config.height +
        (sampleHeight(entry.waves, lx, lz, this.time) +
          (entry.ripples?.heightAtLocal(lx, lz) ?? 0)) *
          shoal
      );
    }

    if (entry.type === 'swe') {
      return (
        entry.config.height +
        entry.solver.surfaceAtLocal(x - entry.mesh.position.x, z - entry.mesh.position.z)
      );
    }

    // Reflective: flat height + optional Gerstner perturbation
    const cfg = entry.config;
    let height = cfg.height;
    if (cfg.gerstnerAmplitude > 0 && cfg.gerstnerOctaves > 0) {
      for (let o = 0; o < cfg.gerstnerOctaves; o++) {
        const scale = Math.pow(2, o);
        const freq = cfg.gerstnerScale * scale;
        const amp = cfg.gerstnerAmplitude / scale;
        height += Math.sin(x * freq * 0.1 + this.time * cfg.gerstnerSpeed * (o + 1)) * amp;
        height += Math.cos(z * freq * 0.13 + this.time * cfg.gerstnerSpeed * (o + 0.7)) * amp * 0.7;
      }
    }
    return height;
  }

  // ─── Per-frame update ───────────────────────────────────────────────

  /** Period for time wrapping — avoids Math.sin precision loss over long sessions. */
  private static readonly _TIME_WRAP = Math.PI * 2 * 1000;

  execute(dt: number, _world: World): void {
    this.time = (this.time + dt) % WaterSystem._TIME_WRAP;

    // Read sun direction once from DayNightCycle (if available)
    const sunDir = this.dayNightCycle
      ? this._sunDir.copy(this.dayNightCycle.getSunDirection())
      : null;

    for (const [, entry] of this.waters) {
      if (entry.type === 'gerstner') {
        // Animate vertex displacement for waves and compute analytical normals.
        this._animateGerstner(entry);
      } else if (entry.type === 'realistic') {
        // Infinite oceans follow the camera, snapped to the finest grid cell
        // so center vertices don't swim against the world-anchored waves.
        if (entry.snapStep !== undefined) {
          let camera: THREE.Camera | undefined;
          try {
            camera = this.renderer.cameras.main;
          } catch {
            camera = undefined;
          }
          if (camera) {
            camera.getWorldPosition(this._camPos);
            entry.mesh.position.x = snapToStep(this._camPos.x, entry.snapStep);
            entry.mesh.position.z = snapToStep(this._camPos.z, entry.snapStep);
          }
        }
        // One clock for GPU + CPU: the shader reads uTime, getWaterHeightAt
        // reads this.time — they can never drift apart.
        entry.handles.uTime.value = this.time;
        entry.handles.uWorldOffset.value.set(entry.mesh.position.x, entry.mesh.position.z);
        // Sky tint follows the time of day (the uniform existed, documented as
        // "drive with the sky tint", and nothing ever wrote it).
        if (this.dayNightCycle) entry.handles.uSkyColor.value.copy(this.dayNightCycle.getSkyColor());
        if (sunDir && !entry.config.sunDirection) {
          entry.handles.uSunDirection.value.copy(sunDir);
          // Authored radiance terms follow the sun: ramp out through dusk,
          // floor at a moonlit 0.04 (a day-constant sea GLOWS at midnight).
          entry.handles.uIlluminance.value =
            Math.max(0.04, Math.min(1, (sunDir.y + 0.05) / 0.25));
        }
        entry.foam?.update(this.time, dt, entry.mesh.position.x, entry.mesh.position.z);
        entry.ocean?.update(this.time);
        if (entry.caustics) {
          entry.caustics.uWorldOffset.value.set(entry.mesh.position.x, entry.mesh.position.z);
          if (sunDir && !entry.config.sunDirection) entry.caustics.uSunDirection.value.copy(sunDir);
          entry.caustics.render(this.renderer.native as never, this.time);
        }
        if (entry.ripples) {
          this._spawnRain(entry, dt);
          entry.ripples.update(dt);
        }
        if (entry.spray) {
          // The emitter chases the most compressed sampled crest; emission
          // scales with how hard the surface is folding there.
          const crest = findCrestPoint(entry.waves, entry.config.size.x, entry.config.size.y, this.time);
          const fold = Math.min(1, Math.max(0, (entry.spray.threshold - crest.jacobian) / 0.4));
          entry.spray.group.position.set(
            crest.x,
            sampleHeight(entry.waves, crest.x, crest.z, this.time),
            crest.z,
          );
          entry.spray.emitter.setEmissionRate(entry.spray.rate * fold);
          entry.spray.emitter.update(dt);
          let sprayCamera: THREE.Camera | undefined;
          try {
            sprayCamera = this.renderer.cameras.main;
          } catch {
            sprayCamera = undefined;
          }
          if (sprayCamera) entry.spray.renderer.update(entry.spray.emitter, sprayCamera);
        }
      } else if (entry.type === 'swe') {
        entry.solver.step(dt);
        this._syncShallowMesh(entry);
      } else {
        // WaterMesh self-animates via TSL time node — just sync sun direction
        if (sunDir) {
          entry.mesh.sunDirection.value.copy(sunDir);
        }
      }
    }

    this._updateUnderwater();
  }

  // ─── Interactive ripples ────────────────────────────────────────────

  /**
   * Splash the surface at a WORLD position (wakes, impacts, drips). No-op
   * unless the body was created with `ripples`. Strength in world units
   * (negative pushes the surface down, like a drop).
   */
  disturbWater(name: string, worldX: number, worldZ: number, strength: number, radius?: number): boolean {
    const entry = this.waters.get(name);
    if (!entry || entry.type !== 'realistic' || !entry.ripples) return false;
    entry.ripples.disturb(worldX - entry.mesh.position.x, worldZ - entry.mesh.position.z, strength, radius);
    return true;
  }

  /** Ambient rain on a body: drops per second (0 stops). Needs `ripples`. */
  setRainRate(name: string, dropsPerSecond: number): boolean {
    const entry = this.waters.get(name);
    if (!entry || entry.type !== 'realistic' || !entry.ripples) return false;
    entry.rainRate = Math.max(0, dropsPerSecond);
    return true;
  }

  private _rainSeed = 421;

  private _spawnRain(entry: RealisticWaterBody, dt: number): void {
    const rate = entry.rainRate ?? 0;
    if (rate <= 0 || !entry.ripples) return;
    // Expected drops this frame; the fractional remainder lands
    // probabilistically via the deterministic LCG.
    const expected = rate * dt;
    let drops = Math.floor(expected);
    this._rainSeed = (this._rainSeed * 1664525 + 1013904223) >>> 0;
    if (this._rainSeed / 0xffffffff < expected - drops) drops++;
    for (let i = 0; i < drops; i++) {
      this._rainSeed = (this._rainSeed * 1664525 + 1013904223) >>> 0;
      const rx = (this._rainSeed / 0xffffffff - 0.5) * entry.config.size.x;
      this._rainSeed = (this._rainSeed * 1664525 + 1013904223) >>> 0;
      const rz = (this._rainSeed / 0xffffffff - 0.5) * entry.config.size.y;
      entry.ripples.disturb(rx, rz, -0.025, 0.18);
    }
  }

  // ─── Underwater (submerged camera) ─────────────────────────────────

  /**
   * The realistic body the point is submerged in, or null. A point counts as
   * underwater when it lies inside the body's XZ footprint and below the
   * live wave surface above it.
   */
  isUnderwater(x: number, y: number, z: number): string | null {
    for (const [name, entry] of this.waters) {
      if (entry.type !== 'realistic') continue;
      if (!entry.config.infinite) {
        const halfX = entry.config.size.x / 2;
        const halfZ = entry.config.size.y / 2;
        const cx = entry.mesh.position.x;
        const cz = entry.mesh.position.z;
        if (x < cx - halfX || x > cx + halfX || z < cz - halfZ || z > cz + halfZ) continue;
      }
      if (y < this.getWaterHeightAt(name, x, z)) return name;
    }
    return null;
  }

  /**
   * Absorption-tinted FogExp2 while the main camera is inside a realistic
   * body (its underwaterFog option granting) — previous fog restored on
   * surfacing. The water material's own underside renders Snell's window;
   * this handles everything ELSE the camera sees down there.
   */
  private _updateUnderwater(): void {
    if (!this.scene) return;
    let camera: THREE.Camera | undefined;
    try {
      camera = this.renderer.cameras.main;
    } catch {
      return; // no camera registered yet
    }
    if (!camera) return;
    camera.getWorldPosition(this._camPos);
    const name = this.isUnderwater(this._camPos.x, this._camPos.y, this._camPos.z);
    const entry = name ? (this.waters.get(name) as RealisticWaterBody | undefined) : undefined;
    // The submerged body drives the OVERLAY; `underwaterFog` only opts the
    // scene-fog swap in or out (it used to gate both, so a body with the
    // overlay enabled and fog disabled never showed its overlay).
    const want = entry;
    const fogWanted = !!entry && (entry.config.underwaterFog ?? true);

    if (want && this._underwaterBody !== want.name) {
      // Body-to-body transition: hide the outgoing overlay (it stayed on
      // forever when swimming straight from one pool into another).
      if (this._underwaterBody !== null) {
        const prev = this.waters.get(this._underwaterBody) as RealisticWaterBody | undefined;
        if (prev?.underwaterOverlay) prev.underwaterOverlay.mesh.visible = false;
      }
      const cfg = want.config;
      const fogColor = new THREE.Color(cfg.deepColor ?? DEFAULT_REALISTIC_WATER.deepColor)
        .lerp(new THREE.Color(cfg.shallowColor ?? DEFAULT_REALISTIC_WATER.shallowColor), 0.3);
      const a = cfg.absorption ?? DEFAULT_REALISTIC_WATER.absorption;
      const density = cfg.underwaterFogDensity ?? (a ? ((a.r + a.g + a.b) / 3) * 0.4 : 0.18);
      const existing = this.scene.fog as THREE.FogExp2 | null;
      if (!fogWanted) {
        // fog opted out — overlay only
      } else if (existing && (existing as { isFogExp2?: boolean }).isFogExp2) {
        // MUTATE the scene's own FogExp2 — object identity preserved, no
        // material rebuilds (fog changes flip needsUpdate on every material).
        if (this._underwaterBody === null) {
          this._prevFogValues = { color: existing.color.getHex(), density: existing.density };
          this._mutatedFog = existing;
        }
        existing.color.copy(fogColor);
        existing.density = density;
      } else {
        // No compatible fog to reuse — a one-time swap (define flip) is
        // unavoidable; at least reuse ONE instance across every submerge
        // (a fresh FogExp2 per dive re-flipped the define = a program rebuild).
        if (this._underwaterBody === null) this._prevFog = this.scene.fog;
        if (!this._ownFog) this._ownFog = new THREE.FogExp2(fogColor, density);
        else { this._ownFog.color.copy(fogColor); this._ownFog.density = density; }
        this.scene.fog = this._ownFog;
      }
      this._underwaterBody = want.name;

      // Submerged-camera screen effect (created eagerly at add time; the
      // shader is pre-warmed so this toggle never compiles a pipeline).
      if (want.underwaterOverlay) want.underwaterOverlay.mesh.visible = true;
    } else if (!want && this._underwaterBody !== null) {
      const prev = this.waters.get(this._underwaterBody) as RealisticWaterBody | undefined;
      if (prev?.underwaterOverlay) prev.underwaterOverlay.mesh.visible = false;
      if (this._mutatedFog && this._prevFogValues) {
        this._mutatedFog.color.setHex(this._prevFogValues.color);
        this._mutatedFog.density = this._prevFogValues.density;
        this._mutatedFog = null;
        this._prevFogValues = null;
      } else if (this.scene.fog === this._ownFog) {
        this.scene.fog = this._prevFog;
        this._prevFog = null;
      }
      this._underwaterBody = null;
    }

    // While submerged: drive the overlay's clock + camera depth (the SAME
    // height query buoyancy uses, so the grading tracks the real surface).
    if (this._underwaterBody !== null) {
      const cur = this.waters.get(this._underwaterBody) as RealisticWaterBody | undefined;
      if (cur?.underwaterOverlay?.mesh.visible) {
        const surfaceY = this.getWaterHeightAt(this._underwaterBody, this._camPos.x, this._camPos.z);
        cur.underwaterOverlay.update(this.time, surfaceY - this._camPos.y);
      }
    }
  }

  /** Heightfield ← solver surface; dry cells tuck just under the bed. */
  private _syncShallowMesh(entry: ShallowWaterBody): void {
    const geo = entry.mesh.geometry;
    const posAttr = geo.getAttribute('position');
    if (!posAttr) return;
    const positions = posAttr.array as Float32Array;
    const n = entry.solver.resolution;
    const { bed, depth } = entry.solver.surfaceData;
    for (let i = 0; i < n * n; i++) {
      const d = depth[i];
      // Dry cells sink 2 cm below the bed so no zero-thickness film shows.
      positions[i * 3 + 1] = d > 1e-4 ? bed[i] + d : bed[i] - 0.02;
    }
    posAttr.needsUpdate = true;
    geo.computeVertexNormals?.();
  }

  /**
   * CPU vertex displacement + analytical normals for Gerstner water.
   */
  private _animateGerstner(water: GerstnerWaterBody): void {
    const cfg = water.config;
    const geo = water.mesh.geometry;
    const posAttr = geo.getAttribute('position');
    let normalAttr = geo.getAttribute('normal');
    if (!posAttr) return;

    const positions = posAttr.array as Float32Array;
    const count = posAttr.count;

    // Ensure we have a writable normal attribute
    if (!normalAttr || normalAttr.count !== count) {
      normalAttr = new THREE.BufferAttribute(new Float32Array(count * 3), 3);
      geo.setAttribute('normal', normalAttr);
    }
    const normals = normalAttr.array as Float32Array;

    for (let i = 0; i < count; i++) {
      const x = positions[i * 3];
      const z = positions[i * 3 + 2];

      let y = 0;
      let dydx = 0;
      let dydz = 0;

      for (let o = 0; o < cfg.waveOctaves; o++) {
        const scale = Math.pow(2, o);
        const freq = cfg.waveScale * scale;
        const amp = cfg.waveAmplitude / scale;

        const phaseX = x * freq * 0.1 + this.time * cfg.waveSpeed * (o + 1);
        const phaseZ = z * freq * 0.13 + this.time * cfg.waveSpeed * (o + 0.7);

        // Height
        y += Math.sin(phaseX) * amp;
        y += Math.cos(phaseZ) * amp * 0.7;

        // Analytical derivatives: d(sin(ax))/dx = a*cos(ax), d(cos(az))/dz = -a*sin(az)
        dydx += Math.cos(phaseX) * amp * freq * 0.1;
        dydz += -Math.sin(phaseZ) * amp * 0.7 * freq * 0.13;
      }

      positions[i * 3 + 1] = y;

      // Normal = normalize(-dydx, 1, -dydz)
      const nx = -dydx;
      const ny = 1;
      const nz = -dydz;
      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
      normals[i * 3] = nx / len;
      normals[i * 3 + 1] = ny / len;
      normals[i * 3 + 2] = nz / len;
    }

    posAttr.needsUpdate = true;
    normalAttr.needsUpdate = true;
  }

  // ─── Disposal ───────────────────────────────────────────────────────

  dispose(): void {
    // Snapshot keys to avoid Map mutation during iteration
    const names = Array.from(this.waters.keys());
    for (const name of names) {
      this.removeWater(name);
    }
    this.parentGroup.removeFromParent();
  }
}
